import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useGetAudioUploadUrl, useCreateAudioRecording } from '@/queries/articles';
import { AUDIO_CONSTRAINTS } from '@/types/article';
import { AudioRecorder } from './AudioRecorder';
import { AudioUploader } from './AudioUploader';
import XIcon from '@/assets/common/x.svg?react';
import MicIcon from '@/assets/common/mic.svg?react';
import UploadIcon from '@/assets/common/upload.svg?react';
import styles from './AddRecordingModal.module.scss';

interface AddRecordingModalProps {
  articleId: string;
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'record' | 'upload';
type UploadState = 'idle' | 'uploading' | 'saving' | 'success' | 'error';

export function AddRecordingModal({ articleId, isOpen, onClose }: AddRecordingModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('record');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [durationSeconds, setDurationSeconds] = useState<number | undefined>();
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const getUploadUrl = useGetAudioUploadUrl();
  const createRecording = useCreateAudioRecording();

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setAudioBlob(null);
    setAudioFile(null);
    setDurationSeconds(undefined);
    setUploadState('idle');
    setUploadProgress(0);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleRecordingComplete = (blob: Blob, duration: number) => {
    setAudioBlob(blob);
    setDurationSeconds(duration);
    setAudioFile(null);
  };

  const handleFileSelect = (file: File, duration?: number) => {
    setAudioFile(file);
    setDurationSeconds(duration);
    setAudioBlob(null);
    // Use filename without extension as default title
    const defaultTitle = file.name.replace(/\.[^/.]+$/, '');
    if (!title) {
      setTitle(defaultTitle);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    const audioSource = audioBlob || audioFile;
    if (!audioSource) {
      setError('Please record or upload an audio file');
      return;
    }

    setError(null);
    setUploadState('uploading');

    try {
      // Determine file details
      const isBlob = !!audioBlob;
      const filename = isBlob ? 'recording.webm' : audioFile!.name;
      const contentType = isBlob ? (audioBlob!.type || 'audio/webm') : audioFile!.type;
      const fileSize = isBlob ? audioBlob!.size : audioFile!.size;

      // Get presigned upload URL
      const { uploadUrl, s3Key } = await getUploadUrl.mutateAsync({
        articleId,
        data: {
          filename,
          content_type: contentType,
          file_size: fileSize,
        },
      });

      // Upload to S3
      const xhr = new XMLHttpRequest();

      await new Promise<void>((resolve, reject) => {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Upload failed'));

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.send(audioSource);
      });

      // Save recording metadata
      setUploadState('saving');

      await createRecording.mutateAsync({
        articleId,
        data: {
          title: title.trim(),
          description: description.trim() || undefined,
          s3_key: s3Key,
          file_size_bytes: fileSize,
          duration_seconds: durationSeconds,
          mime_type: contentType,
        },
      });

      setUploadState('success');

      // Close after a brief delay to show success state
      setTimeout(() => {
        handleClose();
      }, 1000);

    } catch (err) {
      console.error('Error uploading recording:', err);
      setUploadState('error');
      setError(err instanceof Error ? err.message : 'Failed to upload recording');
    }
  };

  const hasAudio = !!(audioBlob || audioFile);
  const isUploading = uploadState === 'uploading' || uploadState === 'saving';
  const canSubmit = title.trim() && hasAudio && !isUploading;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className={styles.modal}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
          >
            <div className={styles.modal__header}>
              <h2 className={styles.modal__title}>Add Audio Recording</h2>
              <button onClick={handleClose} className={styles.modal__close} disabled={isUploading}>
                <XIcon />
              </button>
            </div>

            {uploadState === 'success' ? (
              <div className={styles.modal__success}>
                <div className={styles.modal__successIcon}>&#10003;</div>
                <p>Recording added successfully!</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className={styles.modal__form}>
                {/* Tabs */}
                <div className={styles.modal__tabs}>
                  <button
                    type="button"
                    className={`${styles.modal__tab} ${activeTab === 'record' ? styles['modal__tab--active'] : ''}`}
                    onClick={() => setActiveTab('record')}
                    disabled={isUploading}
                  >
                    <MicIcon />
                    Record
                  </button>
                  <button
                    type="button"
                    className={`${styles.modal__tab} ${activeTab === 'upload' ? styles['modal__tab--active'] : ''}`}
                    onClick={() => setActiveTab('upload')}
                    disabled={isUploading}
                  >
                    <UploadIcon />
                    Upload
                  </button>
                </div>

                {/* Tab Content */}
                <div className={styles.modal__tabContent}>
                  {activeTab === 'record' ? (
                    <AudioRecorder
                      onRecordingComplete={handleRecordingComplete}
                      disabled={isUploading}
                    />
                  ) : (
                    <AudioUploader
                      onFileSelect={handleFileSelect}
                      disabled={isUploading}
                    />
                  )}
                </div>

                {/* Audio selected indicator */}
                {hasAudio && (
                  <div className={styles.modal__audioSelected}>
                    <span className={styles.modal__audioSelectedIcon}>&#10003;</span>
                    {audioBlob ? 'Recording ready' : audioFile?.name}
                  </div>
                )}

                {/* Metadata inputs */}
                <div className={styles.modal__fields}>
                  <div className={styles.modal__field}>
                    <label className={styles.modal__label}>
                      Title <span className={styles.modal__required}>*</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter a title for this recording"
                      className={styles.modal__input}
                      maxLength={AUDIO_CONSTRAINTS.MAX_TITLE_LENGTH}
                      disabled={isUploading}
                    />
                  </div>

                  <div className={styles.modal__field}>
                    <label className={styles.modal__label}>Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Optional description"
                      className={styles.modal__textarea}
                      rows={3}
                      maxLength={AUDIO_CONSTRAINTS.MAX_DESCRIPTION_LENGTH}
                      disabled={isUploading}
                    />
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <p className={styles.modal__error}>{error}</p>
                )}

                {/* Upload progress */}
                {isUploading && (
                  <div className={styles.modal__progress}>
                    <div className={styles.modal__progressBar}>
                      <div
                        className={styles.modal__progressFill}
                        style={{ width: uploadState === 'saving' ? '100%' : `${uploadProgress}%` }}
                      />
                    </div>
                    <span className={styles.modal__progressText}>
                      {uploadState === 'saving' ? 'Saving...' : `Uploading... ${uploadProgress}%`}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className={styles.modal__actions}>
                  <button
                    type="button"
                    onClick={handleClose}
                    className={styles.modal__cancelButton}
                    disabled={isUploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={styles.modal__submitButton}
                    disabled={!canSubmit}
                  >
                    {isUploading ? 'Uploading...' : 'Add Recording'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
